/**
 * Custom bouquet bundle builder (<bundle-builder>).
 * Pointure (native select) -> Forme (radios) -> N custom colour dropdowns (thumbnail + desktop hover preview).
 * Adds the bouquet product (single variant) as ONE cart line via the theme's <product-form-component>,
 * with line-item properties (Pointure, Forme visible; _bundle hidden = IDs variantes chaussettes lus par la
 * fonction Cart Transform qui éclate le bouquet en lignes chaussettes au checkout).
 * Stock comes from the Liquid-embedded catalog (page-load); add-to-cart is the hard safety net (422 on sold-out DENY).
 */
function bbEsc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    try {
      this.catalog = JSON.parse(this.querySelector('script[data-bb="catalog"]').textContent);
    } catch (e) {
      this.catalog = { pointures: [], formes: [], socks: [] };
    }
    if (!this.catalog.pointures?.length || !this.catalog.formes?.length) {
      const pts = [], fms = [];
      for (const s of this.catalog.socks) {
        for (const k of Object.keys(s.v || {})) {
          const [p, f] = k.split('|');
          if (!pts.includes(p)) pts.push(p);
          if (!fms.includes(f)) fms.push(f);
        }
      }
      if (!this.catalog.pointures?.length) this.catalog.pointures = pts;
      if (!this.catalog.formes?.length) this.catalog.formes = fms;
    }

    this.pointureSel = this.querySelector('[data-bb="pointure"]');
    this.formeWrap = this.querySelector('[data-bb="forme"]');
    this.skusInput = this.querySelector('[data-bb="skus"]');
    this.bundleInput = this.querySelector('[data-bb="bundle"]');
    this.hint = this.querySelector('[data-bb="hint"]');
    this.sameBtn = this.querySelector('[data-bb="same-for-all"]');
    this.atcButton = this.querySelector('button[name="add"]');
    this.popup = this.querySelector('[data-bb="popup"]');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.priceEl = this.querySelector('[data-bb="price"]');
    this.compareEl = this.querySelector('[data-bb="compare"]');
    this.low = parseInt(this.dataset.low || '10', 10);
    this.canHover = window.matchMedia?.('(hover: hover)')?.matches ?? true;

    this.combos = Array.from(this.querySelectorAll('.bb-combo')).map((el) => ({
      el,
      trigger: el.querySelector('[data-combo-trigger]'),
      thumb: el.querySelector('[data-combo-thumb]'),
      text: el.querySelector('[data-combo-text]'),
      list: el.querySelector('[data-combo-list]'),
      input: el.querySelector('[data-combo-input]'),
      handle: '',
    }));
    if (!this.pointureSel || !this.combos.length) return;

    this.buildPointure();
    this.buildForme();
    this.applyForme();
    this.rebuildCombos();

    this.pointureSel.addEventListener('change', () => this.rebuildCombos());
    this.formeWrap.addEventListener('change', () => { this.applyForme(); this.rebuildCombos(); });
    if (this.sameBtn) this.sameBtn.addEventListener('click', () => this.applyToAll());

    for (const c of this.combos) {
      c.trigger.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(c); });
    }
    document.addEventListener('click', () => this.closeAll());
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });

    this.recompute();
  }

  get pointure() { return this.pointureSel.value; }
  get forme() { const c = this.formeWrap.querySelector('input:checked'); return c ? c.value : ''; }
  sockByHandle(h) { return this.catalog.socks.find((s) => s.h === h); }
  variantFor(sock, pt, fm) { return sock && sock.v ? sock.v[pt + '|' + fm] : null; }

  /** Forme drives the bouquet variant (Mi-mollet vs Haut-mollet) -> set the cart variant id + price. */
  applyForme() {
    const map = this.catalog.bouquet;
    if (!map || !this.variantIdInput) return;
    const b = map[this.forme] || map[Object.keys(map)[0]];
    if (!b) return;
    this.variantIdInput.value = b.id;
    if (this.priceEl) this.priceEl.textContent = b.price;
    if (this.compareEl) {
      this.compareEl.textContent = b.compare;
      this.compareEl.hidden = !b.hasCompare;
    }
  }

  buildPointure() {
    this.pointureSel.innerHTML = this.catalog.pointures
      .map((p, i) => `<option value="${bbEsc(p)}"${i === 0 ? ' selected' : ''}>${bbEsc(p)}</option>`).join('');
  }
  buildForme() {
    this.formeWrap.innerHTML = this.catalog.formes
      .map((f, i) => `<label><input type="radio" name="properties[Forme]" value="${bbEsc(f)}"${i === 0 ? ' checked' : ''}><span>${bbEsc(f)}</span></label>`).join('');
  }

  /** (re)build the option list for every colour combo for the current pointure/forme. */
  rebuildCombos() {
    const pt = this.pointure, fm = this.forme;
    for (const c of this.combos) {
      let stillValid = false;
      c.list.innerHTML = this.catalog.socks.map((sock) => {
        const v = this.variantFor(sock, pt, fm);
        const avail = !!(v && v.a && v.q > 0);
        let stock = '';
        if (!avail) stock = 'épuisé';
        else if (v.q <= this.low) stock = `plus que ${v.q}`;
        if (sock.h === c.handle && avail) stillValid = true;
        return `<div class="bb-combo__option${avail ? '' : ' bb-combo__option--disabled'}" role="option"
          data-handle="${bbEsc(sock.h)}" data-label="${bbEsc(sock.label)}" data-img="${bbEsc(sock.imgL || sock.img || '')}"
          aria-disabled="${avail ? 'false' : 'true'}"${sock.h === c.handle ? ' aria-selected="true"' : ''}>
          ${sock.img ? `<img class="bb-combo__opt-thumb" src="${bbEsc(sock.img)}" alt="" width="28" height="28" loading="lazy">` : ''}
          <span class="bb-combo__opt-label">${bbEsc(sock.label)}</span>
          ${stock ? `<span class="bb-combo__opt-stock">${bbEsc(stock)}</span>` : ''}
        </div>`;
      }).join('');

      // wire option events
      for (const opt of c.list.querySelectorAll('.bb-combo__option')) {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          if (opt.getAttribute('aria-disabled') === 'true') return;
          this.select(c, opt.dataset.handle, opt.dataset.label, opt.dataset.img);
        });
        if (this.canHover) {
          opt.addEventListener('mouseenter', () => this.showPopup(opt));
          opt.addEventListener('mouseleave', () => this.hidePopup());
        }
      }
      if (!stillValid) this.clear(c);
    }
    this.recompute();
  }

  toggle(c) {
    const open = c.list.hasAttribute('hidden');
    this.closeAll();
    if (open) { c.list.removeAttribute('hidden'); c.trigger.setAttribute('aria-expanded', 'true'); }
  }
  closeAll() {
    this.hidePopup();
    for (const c of this.combos) { c.list.setAttribute('hidden', ''); c.trigger.setAttribute('aria-expanded', 'false'); }
  }

  select(c, handle, label, img) {
    c.handle = handle;
    c.input.value = label;
    c.text.textContent = label;
    c.thumb.innerHTML = img ? `<img src="${bbEsc(img)}" alt="" width="28" height="28">` : '';
    this.closeAll();
    this.markSelected(c);
    this.recompute();
  }
  clear(c) {
    c.handle = ''; c.input.value = ''; c.text.textContent = 'Sélectionner la couleur'; c.thumb.innerHTML = '';
  }
  markSelected(c) {
    for (const opt of c.list.querySelectorAll('.bb-combo__option')) {
      opt.toggleAttribute('aria-selected', opt.dataset.handle === c.handle);
    }
  }

  applyToAll() {
    const first = this.combos[0];
    if (!first || !first.handle) return;
    const sock = this.sockByHandle(first.handle);
    for (const c of this.combos) this.select(c, first.handle, first.input.value, sock ? (sock.imgL || sock.img) : '');
  }

  showPopup(opt) {
    const img = opt.dataset.img;
    if (!img || !this.popup) return;
    this.popup.querySelector('img').src = img;
    this.popup.hidden = false;
    const r = opt.getBoundingClientRect();
    const pw = 216;
    let left = r.left - pw - 8;
    if (left < 8) left = r.right + 8;
    this.popup.style.left = Math.round(left) + 'px';
    this.popup.style.top = Math.round(Math.min(r.top, window.innerHeight - 224)) + 'px';
  }
  hidePopup() { if (this.popup) this.popup.hidden = true; }

  recompute() {
    const pt = this.pointure, fm = this.forme;
    const skus = [];
    const ids = [];
    let complete = true;
    for (const c of this.combos) {
      if (!c.handle) { complete = false; continue; }
      const v = this.variantFor(this.sockByHandle(c.handle), pt, fm);
      if (v && v.sku) skus.push(v.sku); else complete = false;
      if (v && v.id) ids.push(v.id); else complete = false;
    }
    // _skus : SKU des chaussettes (fallback abonnement, non éclaté). _bundle : IDs variantes (éclatement ponctuel).
    if (this.skusInput) this.skusInput.value = skus.join(',');
    if (this.bundleInput) this.bundleInput.value = ids.join(',');
    const ready = complete && !!pt && !!fm && this.combos.every((c) => c.handle);
    if (this.atcButton) this.atcButton.disabled = !ready;
    if (this.hint) {
      const chosen = this.combos.filter((c) => c.handle).length;
      this.hint.textContent = ready ? '' : `Choisissez vos ${this.combos.length} couleurs (${chosen}/${this.combos.length}).`;
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
